import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Dropdown, DropdownBox, List, Popover} from 'tea-component';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';
import {useMemoizedFn, useRequest} from 'ahooks';
import {useParams} from 'umi';
import { calculateDashboardChart, previewDashboardChartSql } from '@dbl/services/dashboard';
import type { IDashboardChartRequest } from '@dbl/services/dashboard';
import {useDblIntl} from '@/pages/datalab/utils/utils';
import Abnormal from '@home/components/Abnormal';
import {SYSTEM_NAME} from '@dbl/core/global';
import './index.scss';
import {
  getScopeFilters,
  hasActiveCompareTime,
} from '@/pages/datalab/components/DashboardLayout/components/DragShell/_utils';
import BigNumberChart from "@customComp/BigNumberChart";
import type { IFilterValueRequest } from '@dbl/services/dashboard';
import { IUserFilterConfig } from '@dbl/core/dashboard';
import {cloneDeep, isEmpty} from 'lodash-es';
import {handleExportClick} from "@/pages/datalab/components/DashboardLayout/_utils";
import { getOldChartDisplayConfig } from '@dbl/core/chartEditor';
import { ChartType } from '@dbl/core/chart';
import TimeCompare from "@/pages/datalab/components/DateComponents/TimeCompare";
import { formatDateRange, getDateRange, type TRelativeTime } from "@dbl/core/date";
import ShowFilterContentIcon, {
  FILTER_CONTENT_KEY
} from "@/pages/datalab/components/ChartCardIconButtons/ShowFilterContentIcon";
import {batchedUpdates} from "@home/utils/utils";
import {useChartCardError} from '@dbl/components/DashboardLayout/components/DragShell/hooks/useChartCardError';
import {transformBigNumberApiData} from "@customComp/BigNumberChart/hooks";
import {useClosePopoversWhenAnchorHidden} from './useClosePopoversWhenAnchorHidden';
import {getCardOverlayProps} from './getCardOverlayProps';
import { usePreviewSQL, PreviewSQLModal } from '@dbl/domainComponents/dataSource';

const BigNumberTragCard = (props: any) => {
  const {
    deleteCard,
    handleOnSubmit,
    dataMsg,
    filterValues,
    userFilter,
    isEdit = true,
    hasChartPrivilege,
    system = 'datalab',
    chartExplore,
    // onForceClose,
    chartsInfoLoading,
    isFilterInit,
  } = props;
  const {task_id: taskId} = dataMsg || {};
  const [open, setOpen] = useState(false);
  const [scopeFilters, setScopeFilters] = useState<any[]>([]);
  const [userFilterInfo, setUserFilterInfo] = useState<IUserFilterConfig | undefined>(undefined);
  
  // 对比时间
  const [compareTimeData, setCompareTimeData] = useState({});
  const cardRef = useRef(null);
  const [previewMoreEntryEl, setPreviewMoreEntryEl] = useState<HTMLDivElement | null>(null);
  const [openVs, setOpenVs] = useState(false);

  const closePreviewPopovers = useMemoizedFn(() => {
    setOpenVs(false);
    setOpen(false);
    cardRef.current?.classList?.remove('dbl-show-card-icon');
  });

  const closeTimeCompareAndMenu = closePreviewPopovers;

  /** 预览态三点菜单：时间对比打开时，鼠标移出菜单也不收起 */
  const handlePreviewDropdownOpenChange = useMemoizedFn((nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      cardRef.current?.classList?.add('dbl-show-card-icon');
      return;
    }
    if (openVs) {
      setOpen(true);
      return;
    }
    setOpen(false);
    cardRef.current?.classList?.remove('dbl-show-card-icon');
  });

  useClosePopoversWhenAnchorHidden(
    previewMoreEntryEl,
    !isEdit && (open || openVs),
    closePreviewPopovers,
  );
  
  const {gamecode}: any = useParams();
  
  const {commonFormatMessage, dblFormatMessage, formatMessage} = useDblIntl('create_dashboard');

  const buildChartParams = useMemoizedFn((queries?: Record<string, unknown>): IDashboardChartRequest => {
    const newValues: IFilterValueRequest[] = [];

    filterValues.forEach(item => {
      if (!isEmpty(item)) { // 由于用户可以新增空的筛选器, 所以在上传的数据里去掉
        if (item.filterType === 'date') {
          let isLastDay = false;
          // 日期筛选器
          const cloneValue = cloneDeep(item); // value是引用类型, 改变value会影响StringFilter内部状态, 所以这里进行了clone
          cloneValue.scope_charts = cloneValue.scope_charts.map((chart) => {
            if (chart.isDefaultDate) { // 开启了Default Date, 要去掉这个参数
              const newChart = {
                ...chart,
                isDefaultDate: undefined,
              };
              // 如果是动态日期, 则将日期筛选器中的value设置为最近1天, 1周, 1月
              // 如果是静态日期, 则将日期筛选器中的value设置为value的第二个元素.

              if (chart.task_id === taskId && isFilterInit) { // 如果是当前卡片, 且是第一次加载, 则将日期筛选器中的value设置为最近1天, 1周, 1月
                isLastDay = true;

                // if (item.operation === 'recent') { // 动态日期类型
                //   // 且选中的不是 当天, 本周, 本月.
                //   if (!cloneValue.value?.[0]?.startsWith('-1')) {
                //     cloneValue.value = [`1-${cloneValue.dateType}`];
                //   }
                // } else {
                //   cloneValue.value = calculateLastPeriod(cloneValue.dateType, item.value);
                // }
              }
              return newChart;
            }
            return chart;
          });
          isLastDay && (cloneValue.is_last_day = isLastDay);
          newValues.push(cloneValue);
        } else {
          newValues.push(item);
        }
      }
    });

    return {
      game_code: gamecode,
      task_id: [String(taskId)],
      queries: {
        filters: newValues,
        user_filter: userFilter,
        ...queries,
      },
      system,
      version: 'v2',
      is_sql_preview: false,
    };
  });

  const {
    previewSQL,
    loading: previewSqlLoading,
    error: previewSqlError,
    modalVisible: previewSqlModalVisible,
    hideModal: hidePreviewSqlModal,
    sqlContent: previewSqlContent,
  } = usePreviewSQL({
    fetchPreviewSql: () =>
      previewDashboardChartSql(ChartType.BigNumber, buildChartParams()),
  });
  
  const requestData = useMemoizedFn((queries?: Record<string, unknown>) => {
    return calculateDashboardChart(ChartType.BigNumber, buildChartParams(queries));
  });
  // 使用useRequest 可以减少loading和error的额外设置代码, 且在类目快速点击切换时, 可以竞态取消，当上一次请求还没返回时，又发起了下一次请求，则会取消上一次请求
  const {data, loading, error, run: getData}: any = useRequest(requestData, {
    manual: true,
    onError: (e) => {
      console.log('获取单指标卡片信息错误: ', e);
    },
    onSuccess: (data) => {
      batchedUpdates(() => { // 批量更新
        // 获取scope的筛选器信息
        const allFilters = data?.[taskId]?.filters || [];
        const dateFilters = allFilters?.filter(item => item.column_type === 'date') || [];
        // 和后端确认过, is_vs_compare_time 为true时, 代表是vs图标中的base time 筛选器字段
        // const compareFilter = dateFilters.find(item => !isEmpty(item?.compare_time));
        const compareFilter = dateFilters.find(item => item.is_vs_compare_time);
        const baseTimeFilter = compareFilter || dateFilters[0];
        
        if (baseTimeFilter) {
          setCompareTimeData({
            filterId: baseTimeFilter?.filterKey || baseTimeFilter?.table_id,
            defaultCompareTimes: Array.isArray(baseTimeFilter?.compare_time) ? baseTimeFilter?.compare_time : [],
            baseTimeRange: Array.isArray(baseTimeFilter?.value) ? baseTimeFilter?.value : [],
            baseTimeName: baseTimeFilter?.filter_name || baseTimeFilter?.display_name || baseTimeFilter?.column,
            timeGranularity: baseTimeFilter?.dateType,
            chartFilter: baseTimeFilter,
          });
        } else {
          // 说明不存在 日期筛选器控制图表, 此时不显示vs图标
          setCompareTimeData({});
        }
        // 获取scope的筛选器信息
        setScopeFilters(getScopeFilters(allFilters));
        // 获取 user fitler的配置
        setUserFilterInfo(data?.[taskId]?.user_filter);
      });
    }
  });
  
  useEffect(() => {
    !chartsInfoLoading && (filterValues || userFilter?.controls?.length) && getData();
  }, [filterValues, chartsInfoLoading, userFilter]);
  
  const {curMetricsName, isNoData , trendStatus , staticTime, type,
    componentProps} = useMemo(() => {
    if (!data) return {};
    const chartData = data?.[taskId] || {};
    let metricInfo = chartData?.column_name?.find(item => item?.name === chartData?.metrics?.[0]) || {};
    const { accuracy, unit } = getOldChartDisplayConfig({...metricInfo, column_type: metricInfo.column_date_type}, ChartType.Table);
    metricInfo = {...metricInfo, accuracy, unit};
    const { showStaticTime = false } = JSON.parse(dataMsg?.column_infos || '{}')?.trendStatus || {};
    const trendStatus = {
      showStaticTime,
    };
    /**
     * 1. 针对报表配置中的 isDefaultLastDate判断逻辑
     *
     * 前提
     *    在图表中 将 create_time日期字段设置为 Filter, 且设置最近14天;
     * 场景:
     *    在报表中无论设置多少个日期字段作用于 数字卡片; 只要有一个设置了Default Last Date;
     * 结论:
     *     那么 数字卡片 就展示单点的指标值
     *     (指标值 取 接口中的 last_date_value)
     */
    let isDefaultLastDate = false;
    // 遍历报表筛选器,
    filterValues.forEach((filter) => {
      filter.scope_charts?.forEach((scopeChart) => {
        // 查找报表筛选器是否作用于当前数字卡片
        if (String(scopeChart.task_id) === String(taskId)) {
          isDefaultLastDate = isDefaultLastDate || scopeChart.isDefaultDate;
        }
      });
    });
  
    // 2. 指标名称
    const curMetricsName = data?.[taskId]?.metrics?.[0]?.replace(/(t[0-2]_)/, '') || '';
    
    const {type, componentProps, staticTime, isNoData} = transformBigNumberApiData({bigNumbers: chartData?.big_number_charts, displayConfig: {
      accuracy: metricInfo?.accuracy || 0,
      unit: metricInfo?.unit || 'bmk',
      columnType: metricInfo?.column_date_type,
    } });

    return {
      isNoData,
      // 产品修改需求: 不需要显示聚合名称
      // curMetricsName: curMetricsName + `(${MetricTypes[chartData?.trend_metric?.aggregation]?.[isEn ? 'text_en' : 'text']})`,
      curMetricsName,
      trendStatus,
      type,
      componentProps,
      staticTime
    };
  }, [data]);
  
  const jumpToEdit = () => {
    // 重置离开弹窗操作在最外层index中
    // dispatch({
    //   type: 'customDashboard/save',
    //   payload: {
    //     isHideCustomLeaveModal: true,
    //   }
    // })
    const pathId = dataMsg.type === 1 ? 'Action/ExploreAnalysis' : 'Analysis/MultiAnalysis';
    const path = `/${SYSTEM_NAME}/${gamecode}/${pathId}?from=edit&taskId=${dataMsg.task_id}&name=${dataMsg?.name}&nameCn=${dataMsg?.name_cn}`;
  
    handleOnSubmit?.(path);
  };
  
  const failedMsg = useChartCardError(error);

  const overlayProps = getCardOverlayProps(false, cardRef.current);
  const isTimeCompareActive = useMemo(
    () => hasActiveCompareTime(compareTimeData, filterValues),
    [compareTimeData, filterValues],
  );

  return (
    <div ref={cardRef} className="dbl-card-drag table-analysis-chart dbl-metric-card">
      {/* Abnormal中right占位符宽度需要和定位的dbl-metric-card-operation的宽度保持一致 */}
      <div className={classNames('dbl-card-icon-operation-box', 'dbl-metric-card-operation', !isEdit && 'dbl-card-preview-toolbar')}>
        {
          !isEdit && (!loading && !error) ? (
            <Dropdown
              trigger="hover"
              clickClose={false}
              boxClassName={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
              button={
                <div
                  ref={setPreviewMoreEntryEl}
                  className={classNames('dbl-card-drag-operation', 'dbl-card-preview-more-entry', {action: open})}
                >
                  <SvgIcon type="there-points" />
                </div>
              }
              placementOffset={0}
              open={open}
              appearance="pure"
              placement="bottom-end"
              onOpenChange={handlePreviewDropdownOpenChange}
              {...overlayProps}
            >
              <List type="option">
                {compareTimeData?.filterId ? (
                  <Popover
                    trigger="click"
                    placement="right-start"
                    visible={openVs}
                    {...overlayProps}
                    onVisibleChange={(vsOpen) => {
                      if (vsOpen) {
                        setOpenVs(true);
                        cardRef.current?.classList?.add('dbl-show-card-icon');
                      }
                      // 不响应 false：仅能通过确定/取消关闭，避免移出菜单或误点外部被收起
                    }}
                    overlay={
                      <DropdownBox
                        className={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-timecompare-dropdown')}
                        preventMouseDown
                      >
                        <TimeCompare
                          openSingle={true}
                          defaultCompareTimes={compareTimeData.defaultCompareTimes}
                          baseTimeRange={compareTimeData.baseTimeRange}
                          baseTimeName={compareTimeData.baseTimeName}
                          timeGranularity={compareTimeData.timeGranularity}
                          onConfirm={(compareTimes: TRelativeTime[]) => {
                            let newCompareTimes: TRelativeTime[] = [];
                            if (compareTimes && compareTimes.length > 0) {
                              newCompareTimes = compareTimes.map((compare) => {
                                if (compare?.time_range && isEmpty(compare.last_time)) return compare;
                                const {last_time: lastTime, granularity} = compare || ({} as any);
                                const range = getDateRange(compareTimeData.baseTimeRange || [], {
                                  amount: lastTime,
                                  granularity,
                                  timeGranularity: compareTimeData.timeGranularity,
                                });
                                return {
                                  ...compare,
                                  time_range: formatDateRange(range, compareTimeData.timeGranularity),
                                } as any;
                              });
                            }

                            if (compareTimeData?.chartFilter?.filter_name) {
                              const newFilters = (filterValues ?? []).map((item: any) => {
                                if (item.filterKey === compareTimeData.filterId) {
                                  return {
                                    ...item,
                                    compare_time: newCompareTimes
                                  };
                                }
                                return item;
                              });
                              getData({filters: newFilters});
                            } else {
                              getData({filters: [
                                ...(filterValues ?? []),
                                {
                                  ...(compareTimeData.chartFilter || {}),
                                  compare_time: newCompareTimes,
                                },
                              ]});
                            }
                            closeTimeCompareAndMenu();
                          }}
                          onCancel={closeTimeCompareAndMenu}
                        />
                      </DropdownBox>
                    }
                  >
                    <List.Item
                      className={classNames(
                        'dbl-card-preview-vs-menu-item',
                        isTimeCompareActive && 'dbl-card-preview-vs-menu-item--active',
                      )}
                    >
                      <SvgIcon type="vs-icon"/>
                      <span className="dbl-card-preview-vs-menu-text">
                        {formatMessage({id: 'dbl_time_compare_title'})}
                      </span>
                    </List.Item>
                  </Popover>
                ) : null}
                <List.Item className="dbl-card-preview-filter-menu-item">
                  <ShowFilterContentIcon
                    contentMap={{
                      [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                      [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                    }}
                    isEdit={true}
                    overlayClassName="dbl-card-preview-more-filter-overlay"
                    triggerEndContent={
                      <span className="dbl-card-preview-more-menu-label">
                        {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                      </span>
                    }
                    onVisibleChange={(visible: boolean) => {
                      if (visible) {
                        cardRef.current?.classList?.add('dbl-show-card-icon');
                      } else {
                        cardRef.current?.classList?.remove('dbl-show-card-icon');
                      }
                    }}
                  />
                </List.Item>
                {
                  chartExplore && (dataMsg.type === 0) ? (
                    <List.Item
                      className="dbl-card-preview-explore-menu-item"
                      onClick={() => {
                        handleExportClick({
                          filters: data?.[taskId]?.filters,
                          system,
                          gamecode,
                          dataMsg,
                          userFilterInfo
                        });
                        setOpen(false);
                      }}
                    >
                      <SvgIcon type="explore" />
                      {dblFormatMessage('icon_explore')}
                    </List.Item>
                  ) : null
                }
                <List.Item
                  onClick={() => {
                    previewSQL();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="open-eyes"/>
                  {formatMessage({ id: 'dbl_multi_preview_sql' })}
                </List.Item>
              </List>
            </Dropdown>
          ) : null
        }
        {
          isEdit && <Dropdown
            trigger="hover"
            clickClose={false}
            boxClassName={'dbl-card-drag-operation-dropdown'}
            button={
              <div className={classNames('dbl-card-drag-operation', {action: open})}>
                <SvgIcon type="there-points" />
              </div>
            }
            placementOffset={0}
            open={open}
            appearance="pure"
            placement="bottom-end"
            onOpenChange={(value) => {
              setOpen(value);
            }}
          >
            <List type="option">
              {
                (hasChartPrivilege && dataMsg?.edit) ? (<List.Item
                  onClick={() => {
                    jumpToEdit();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="pencil-m" />{commonFormatMessage('edit')}
                </List.Item>
                ) : (
                  ''
                )}
              <List.Item
                onClick={() => {
                  previewSQL();
                  setOpen(false);
                }}
              >
                <SvgIcon type="open-eyes"/>
                {formatMessage({ id: 'dbl_multi_preview_sql' })}
              </List.Item>
              <List.Item
                onClick={() => {
                  setOpen(false);
                  deleteCard(dataMsg);
                }}
              >
                <SvgIcon type="trash-can-icon" />{commonFormatMessage('delete')}
              </List.Item>
            </List>
          </Dropdown>
        }
      </div>
      <Abnormal
        style={{
          width: '100%',
          height: '100%'
        }}
        isLoading={loading || chartsInfoLoading}
        isError={error}
        nodataMsg={commonFormatMessage("no_filter_data")}
        failedMsg={failedMsg}
        isNoData={isNoData ?? true}
        component={<BigNumberChart
          icon={'plate'}
          type={type}
          componentProps={componentProps}
          title={curMetricsName}
          subTitle={trendStatus?.showStaticTime ? staticTime : undefined}
          className={'dashboard'}
          // 该占位符宽度需要和定位的dbl-metric-card-operation的宽度保持一致
          right={<div className='dbl-metric-card-placeholder' />}
        />}
      />
      <PreviewSQLModal
        visible={previewSqlModalVisible}
        onClose={hidePreviewSqlModal}
        sql={previewSqlContent}
        loading={previewSqlLoading}
        error={previewSqlError}
      />
    </div>
  );
};

export default BigNumberTragCard;
